import { Button } from '@neo/primitives/button'
import { Checkbox } from '@neo/primitives/checkbox'
import { Input } from '@neo/primitives/input'
import { Popover, PopoverContent, PopoverTrigger } from '@neo/primitives/popover'
import { ScrollArea } from '@neo/primitives/scroll-area'
import { Separator } from '@neo/primitives/separator'
import { ChevronDown } from 'lucide-react'
import { useMemo, useState } from 'react'
import { useParams } from 'router-v6'
import { useGetKnowledgeBases } from '../knowledge-base/queries'
import { useFlowStore } from '../store'
import { useLinkKnowledgeBaseMutation } from './mutations'

export const LinkKnowledgeBasePopover = () => {
  const { id = '' } = useParams()
  const [open, setOpen] = useState(false)
  const [selectedIds, setSelectedIds] = useState<string[]>([])

  const setAgentSettings = useFlowStore((state) => state.setAgentSettings)

  const { data: knowledgeBases, isLoading } = useGetKnowledgeBases()
  const { mutate: linkKnowledgeBases, isPending } = useLinkKnowledgeBaseMutation()

  // The KBs already linked to THIS agent, derived from the SAME source the list uses
  // (kb.phlo_uuids includes the agent id) — not the flow store's knowledge_base_ids, which on a
  // fresh page load isn't seeded with the existing links, so the boxes never pre-checked. This
  // keeps the popover's checked state consistent with the "Associated Agents" list.
  const linkedIds = useMemo(
    () =>
      (knowledgeBases ?? []).reduce<string[]>((ids, kb) => {
        if (kb.phlo_uuids?.includes(id)) ids.push(kb.id)
        return ids
      }, []),
    [knowledgeBases, id],
  )

  // If the agent-link reverse-read failed, kb.phlo_uuids is non-authoritative (coalesced to []), so
  // `linkedIds` may be wrongly empty. Saving would full-replace knowledge_base_ids and silently drop
  // existing links — block Save until a refetch succeeds rather than corrupt the association set.
  const linksUnavailable = (knowledgeBases ?? []).some((kb) => kb.linksUnavailable)

  const handleOpenChange = (isOpen: boolean) => {
    if (isOpen) setSelectedIds([...linkedIds])
    setOpen(isOpen)
  }

  const handleToggle = (kbId: string, checked: boolean) => {
    setSelectedIds((prev) => (checked ? [...prev, kbId] : prev.filter((id) => id !== kbId)))
  }

  const handleSave = () => {
    if (!id || linksUnavailable) return

    linkKnowledgeBases(
      { knowledgeBaseIds: selectedIds, flowId: id },
      {
        onSuccess: () => {
          setAgentSettings({ knowledge_base_ids: selectedIds })
          setOpen(false)
        },
      },
    )
  }

  const [searchTerm, setSearchTerm] = useState('')
  const filteredKbs = useMemo(() => {
    return knowledgeBases?.filter((kb) => kb.name.toLowerCase().includes(searchTerm.toLowerCase()))
  }, [searchTerm, knowledgeBases])
  const hasKnowledgeBases = knowledgeBases && knowledgeBases.length > 0

  if (!isLoading && !hasKnowledgeBases) return null

  return (
    <Popover open={open} onOpenChange={handleOpenChange} modal>
      <PopoverTrigger asChild>
        <Button variant="outline" disabled={isLoading} data-testid="button-link-knowledge-base">
          Link Knowledge Base
          <ChevronDown size={16} className="ml-2" />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        align="end"
        className="w-80 overflow-hidden rounded-lg border-border bg-popover p-0 shadow-lg"
      >
        {hasKnowledgeBases ? (
          <div className="p-4">
            <p className="mb-3 text-s-500 text-foreground">Link knowledge bases</p>
            <Input
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search KBs"
            />
            <ScrollArea className="mt-3 h-[300px]">
              <div className="flex flex-col gap-1 pr-3">
                {filteredKbs?.map((kb) => (
                  <div key={kb.id} className="rounded-md px-2 py-2 hover:bg-hv1">
                    <Checkbox
                      id={kb.id}
                      name={kb.id}
                      label={kb.name}
                      value={selectedIds.includes(kb.id)}
                      onChange={(checked) => handleToggle(kb.id, checked)}
                      data-testid={`checkbox-kb-${kb.id}`}
                    />
                  </div>
                ))}
              </div>
            </ScrollArea>
            <Separator />
          </div>
        ) : null}
        <div className="flex justify-end gap-2 border-t border-border bg-card p-3">
          <Button
            variant="outline"
            onClick={() => setOpen(false)}
            disabled={isPending}
            data-testid="button-cancel-link-kb"
            size="sm"
          >
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            disabled={isPending || linksUnavailable}
            title={
              linksUnavailable
                ? "Couldn't load the agent's current Knowledge Bases. Reopen to retry before saving."
                : undefined
            }
            data-testid="button-save-link-kb"
            size="sm"
          >
            {isPending ? 'Saving...' : 'Save'}
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  )
}
